import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Video } from 'lucide-react';
const videos = [{
  src: 'https://cdn.magicpatterns.com/uploads/gsKZ2c9EmwEJrQCUTGjeNA/F375FEC6-B062-4EDC-91B5-83D48B2188AF.mp4',
  title: 'Construction Debris'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/4fnzC6XGJpcJtg5L6XRJxY/C58F56D9-3213-4E6D-B553-2A34ED969E24.mov',
  title: 'Yard Waste & Furniture'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ccAMUiLTw9RecFqAq3abUA/85FAFDAB-E1AB-4EDE-819A-7815D37361E7.mov',
  title: 'Light Demolition'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nz8QqMj8cScMhYa6d3Amc8/3283C7D2-1619-484C-9020-56A2DC7E73F4.MOV',
  title: 'Hot Tub Removal'
}];
function VideoPlayer({
  src,
  title
}: {
  src: string;
  title: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  return <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black aspect-[9/16] group w-full max-w-[300px] mx-auto sm:max-w-none flex-shrink-0 snap-center" data-id="element-587" data-mp="i9h5hdw9cdlh">
      <video ref={videoRef} src={src} className="w-full h-full object-cover" autoPlay loop muted playsInline data-id="element-588" data-mp="3lod6xp0vlb59" />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 pointer-events-none" data-id="element-589" data-mp="3uq47kfgk6rs9" />

      {/* Title */}
      <div className="absolute top-4 left-4 right-4 z-10" data-id="element-590" data-mp="19y12q8018jyv">
        <span className="inline-block px-3 py-1 rounded-full bg-black/50 backdrop-blur-md border border-white/10 text-white text-[10px] font-bold uppercase tracking-widest" data-id="element-591" data-mp="1a0nao8qu51hm">
          {title}
        </span>
      </div>

      {/* Controls */}
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between z-10 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300" data-id="element-592" data-mp="3g0ejg372bqox">
        <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-white/20 hover:bg-[#C8102E] backdrop-blur-md flex items-center justify-center text-white transition-colors" aria-label={isPlaying ? 'Pause video' : 'Play video'} data-id="element-593" data-mp="2nb7dij5a3ytn">
          {isPlaying ? <Pause size={18} className="fill-current" data-id="element-594" data-mp="2sak8xsa7ue7u" /> : <Play size={18} className="fill-current ml-1" data-id="element-595" data-mp="2s87hmoikiri4" />}
        </button>

        <button onClick={toggleMute} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md flex items-center justify-center text-white transition-colors" aria-label={isMuted ? 'Unmute video' : 'Mute video'} data-id="element-596" data-mp="24iwq7xn3y9gn">
          {isMuted ? <VolumeX size={18} data-id="element-597" data-mp="jvvluvr0tqj9" /> : <Volume2 size={18} data-id="element-598" data-mp="jwf4yfseqkyv" />}
        </button>
      </div>
    </div>;
}
export function VideoShowcase() {
  return <section className="py-20 lg:py-28 bg-[#1A1A1A] relative overflow-hidden" data-id="element-599" data-mp="b20gpnpwg03m">
      <div className="absolute inset-0 bg-grid-faint opacity-[0.03] pointer-events-none" data-id="element-600" data-mp="azwa2y0m8mzn" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#C8102E] opacity-[0.05] blur-[150px] rounded-full pointer-events-none" data-id="element-601" data-mp="ypgxbt3sy6eq" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-602" data-mp="osc97nqec0va">
        {/* Text Content */}
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-603" data-mp="trfg7dpxsh5w">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-6" data-id="element-604" data-mp="39jmkjvwz5foh">
            <Video size={14} className="text-[#C8102E]" data-id="element-605" data-mp="3p1w6hdflq8tg" />
            <span className="text-[11px] tracking-[0.25em] font-bold uppercase text-gray-300" data-id="element-606" data-mp="3p0cptlq19vr5">
              On The Job
            </span>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-white leading-[1.05] mb-6" data-id="element-607" data-mp="3g1qz33aerafa">
            SEE THE CREW <span className="text-[#C8102E]" data-id="element-608" data-mp="ciejykcae9t9">IN ACTION.</span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-gray-400 leading-relaxed" data-id="element-609" data-mp="7n8x9jb28r44">
            We don't just talk about hard work — we document it. From breaking down cabinets to cutting up hot tubs, our crew handles the heavy lifting so you don't have to.
          </motion.p>
        </div>

        {/* Videos Grid / Scroll */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative" data-id="element-610" data-mp="2i53thtlpkkl1">
          {/* Mobile: Horizontal Scroll, Desktop: 4-col Grid */}
          <div className="flex overflow-x-auto pb-8 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 snap-x snap-mandatory scrollbar-hide" data-id="element-611" data-mp="30xerb91i9sca">
            {videos.map((video, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3 + idx * 0.1
          }} className={`w-[85vw] sm:w-auto flex-shrink-0 snap-center ${idx % 2 !== 0 ? 'lg:mt-8' : ''}`} data-id="element-612" data-mp="3awdnhs533vie" data-mpi={idx}>
                <VideoPlayer src={video.src} title={video.title} data-id="element-613" data-mp="13jls0vmapfjx" />
              </motion.div>)}
          </div>

          {/* Decorative elements */}
          <div className="hidden lg:block absolute -top-4 -right-4 w-24 h-24 bg-[radial-gradient(circle,rgba(200,16,46,0.4)_1px,transparent_1px)] bg-[size:8px_8px] -z-10" data-id="element-614" data-mp="1dgrj43uwt6k5" />
          <div className="hidden lg:block absolute -bottom-4 -left-4 w-32 h-32 bg-[radial-gradient(circle,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:8px_8px] -z-10" data-id="element-615" data-mp="1re6b9gcjrsft" />
        </motion.div>
      </div>
    </section>;
}